// Display-only receipts. Transport authorization continues to use the server's fresh checks.
export const requestIdentity = row => row?.requestId || row?.id;
export const activeRequest = row => ["queued", "running", "unknown"].includes(row?.status);
const stamp = row => Date.parse(row?.updatedAt || row?.approvedAt || row?.requestedAt || "") || 0;
const newer = (a, b) => {
  if (Number.isFinite(a?.revision) && Number.isFinite(b?.revision)) {
    if (a.revision !== b.revision) return a.revision > b.revision;
    if (Number.isFinite(a.progress?.journalRevision) && Number.isFinite(b.progress?.journalRevision))
      return a.progress.journalRevision >= b.progress.journalRevision;
    if (b.progress && !a.progress) return false;
    return true;
  }
  return stamp(a) >= stamp(b);
};

export function mergeRequests(boardRows = [], receipts = []) {
  const rows = new Map(boardRows.map(row => [requestIdentity(row), row]));
  for (const receipt of receipts) {
    const id = requestIdentity(receipt);
    if (!id) continue;
    const old = rows.get(id);
    if (!old || newer(receipt, old)) rows.set(id, { ...old, ...receipt, requestId: id });
  }
  return [...rows.values()];
}

export function withRequestProgress(board, requests = [], complete = false) {
  if (!board || (!requests.length && !complete)) return board;
  const ids = new Set(requests.map(requestIdentity));
  const merged = mergeRequests(complete ? (board.requests || []).filter(row => ids.has(requestIdentity(row))) : board.requests, requests);
  return { ...board, requests: merged, packages: board.packages.map(pkg => {
    const old = new Map((pkg.openRequests || []).filter(row => !complete || ids.has(requestIdentity(row))).map(row => [requestIdentity(row), row]));
    for (const request of merged) {
      if (!(request.items || []).some(item => item.packageId === pkg.packageId)) continue;
      const id = requestIdentity(request);
      if (["pending", "queued", "running", "unknown"].includes(request.status)) old.set(id, {
        ...old.get(id), requestId: id, sourceStage: request.sourceStage, targetStage: request.targetStage,
        status: request.status, targets: request.targets, pinDigest: request.pinDigest,
      });
      else old.delete(id);
    }
    // A receipt may update feedback, never target content or the card's stage.
    const source = pkg.card?.stage;
    const scoped = merged.filter(request => request.sourceStage === source
      && request.items?.some(item => item.packageId === pkg.packageId && item.sourceScopeDigest
        && item.sourceScopeDigest === pkg.cells?.[source]?.scopeDigest))
      .sort((a, b) => (Date.parse(b.requestedAt) || 0) - (Date.parse(a.requestedAt) || 0));
    const receipt = scoped[0], item = receipt?.items?.find(row => row.packageId === pkg.packageId);
    const status = receipt?.status === "failed" && item?.status !== "completed" ? "failed"
      : item?.status === "not_started" && receipt?.status === "partial" ? "failed"
      : ["completed", "failed", "partial", "unknown", "running", "queued"].includes(item?.status) ? item.status : receipt?.status;
    const progressRequest = receipt ? { ...receipt, status,
      execution: item?.execution?.error ? item.execution : receipt.execution,
      progress: item?.progress || receipt.progress } : null;
    return { ...pkg, openRequests: [...old.values()], progressRequest };
  }) };
}

export function mergePreparations(previous = [], next = []) {
  const rows = new Map(previous.map(row => [row.id, row]));
  for (const row of next) if (row?.id && (!rows.has(row.id) || newer(row, rows.get(row.id)))) rows.set(row.id, row);
  return [...rows.values()];
}

// Der Server archiviert nur abgeschlossene oder gescheiterte Vorbereitungen und frühestens 24 h nach ihrer letzten Änderung
// (ARCHIVE_AFTER_MS in preparations.js); laufende bleiben immer gelistet. Fehlt eine so alte Zeile in einer vollständigen
// Liste, ist sie archiviert und verlässt die Anzeige (Abnahme DEV252, E). Über ihren Ausgang sagt das nichts; den zeigt der
// Antrag selbst. Jüngere fehlende Zeilen bleiben, eine verzögerte Verzeichnisliste eines anderen Tasks widerruft nichts.
export const PREPARATION_ARCHIVE_AFTER_MS = 24 * 60 * 60 * 1000;
export function withoutArchivedPreparations(rows = [], listed = [], complete = false, now = Date.now()) {
  if (!complete) return rows;
  const ids = new Set(listed.map(row => row?.id));
  // Ohne Zeitstempel ist das Alter unbekannt; die Zeile bleibt.
  return rows.filter(row => ids.has(row.id) || !stamp(row) || now - stamp(row) < PREPARATION_ARCHIVE_AFTER_MS);
}

// Failed request preparations are notices, never transport facts (server: FAILED_REQUEST_NOTICE_MS in preparations.js).
// A hidden, expired or replaced notice leaves the view; the server keeps the row and its explicit retry.
export const FAILED_PREPARATION_NOTICE_MS = 24 * 60 * 60 * 1000;
const packagesOf = row => row?.packageIds || (row?.items || []).map(item => item.packageId);
const sameRoute = (a, b) => a.sourceStage === b.sourceStage && a.targetStage === b.targetStage
  && packagesOf(a).some(id => packagesOf(b).includes(id));
export function requestPreparationNotices(preparations = [], requests = [], now = Date.now()) {
  const rows = preparations.filter(row => row?.action === "request");
  return rows.filter(row => {
    if (row.status === "preparing") return true;
    if (row.status !== "failed" || row.dismissedAt) return false;
    const failedAt = stamp(row);
    if (failedAt && now - failedAt > FAILED_PREPARATION_NOTICE_MS) return false;
    // Only the newest attempt per route and package is shown; a later request on that route answers the notice.
    // Deliberately for the whole notice (David, 05.11.2026): a new request that shares one package with a failed
    // multi-package attempt replaces that attempt as a new transport iteration, so the older one never overrides the newer.
    if (rows.some(other => other.id !== row.id && !other.dismissedAt && sameRoute(other, row) && stamp(other) > failedAt)) return false;
    return !requests.some(request => sameRoute(request, row) && (Date.parse(request.requestedAt || "") || 0) > failedAt);
  });
}
